import { isMac } from "../common/browser";

type ShortcutAction = (ev: KeyboardEvent) => void | Promise<unknown>;

export interface IKeyPattern {
    key: string;
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    /** Overrides the displayed key (For example, even though the binding will be on the = key next to backspace, show '+' instead) */
    displayKey?: string;
}

export interface IShortcutOptions {
    dontPreventDefault?: boolean;
    actionName?: string;
    actionDescription?: string;
    actionCategory?: string;
    alias?: string;
}

export interface IRegisteredShortcut {
    keyPatterns: IKeyPattern[];
    action: ShortcutAction;
    options: IShortcutOptions;
}

type SerializableShortcut = Omit<IRegisteredShortcut, "action">;

export const shortcutCategories = {
    canvas: "Canvas",
    editor: "Editor",
    layers: "Layers",
    selection: "Selection",
    tools: "Tools",
    undoRedo: "Undo/Redo",
    zoom: "Zoom",
}

let lastEvent: KeyboardEvent | null = null

const ShortcutManager = {
    registeredShortcuts: [] as IRegisteredShortcut[],

    Initialize() {
        document.addEventListener("keydown", ev => ShortcutManager.OnKeyDown(ev));
        try {
            window.top.document.addEventListener("keydown", ev => ShortcutManager.OnKeyDown(ev));
        } catch (e) {
            console.error(e);
        }
    },

    RegisterShortcut(keyPatterns: IKeyPattern[] | IKeyPattern | string, action: ShortcutAction, options?: IShortcutOptions) {
        if (typeof keyPatterns === 'string') {
            const kpSpl = keyPatterns.toLowerCase().split('/');
            keyPatterns = [];

            for (const kp of kpSpl) {
                const spl = kp.split('+');
                const newKp = { key: spl[spl.length - 1] } as IKeyPattern;

                for (const s of spl) {
                    if (s === "ctrl" || s === "meta") {
                        newKp.ctrl = true;
                    }

                    if (s === "alt") {
                        newKp.alt = true;
                    }

                    if (s === "shift") {
                        newKp.shift = true;
                    }
                }

                keyPatterns.push(newKp);
            }
        }

        if (!Array.isArray(keyPatterns)) {
            keyPatterns = [keyPatterns];
        }

        if (keyPatterns.length == 0) {
            return;
        }

        for (let i = 0; i < keyPatterns.length; ++i) {
            keyPatterns[i] = {
                ...keyPatterns[i],
                key: keyPatterns[i].key.toLowerCase(), // Done this way to avoid mutating the original object
            }
        }

        // Check for dupes
        for (const s of ShortcutManager.registeredShortcuts) {
            for (const skp of s.keyPatterns) {
                for (const kp of keyPatterns) {
                    if (skp.key === kp.key
                        && skp.ctrl === kp.ctrl
                        && skp.alt === kp.alt
                        && skp.shift === kp.shift
                    ) {
                        console.error("Tried to register a duplicate shortcut! Ignoring the new shortcut. Existing shortcut:", s, " New Shortcut: ", keyPatterns);
                        return;
                    }
                }
            }
        }

        ShortcutManager.registeredShortcuts.push({
            keyPatterns,
            action,
            options: options ?? {},
        });
    },

    GetAllShortcuts(invokedFromBlazor: boolean = false): IRegisteredShortcut[] | SerializableShortcut[] {
        if (invokedFromBlazor) {
            // Remove methods since they can't be serialized.
            return [
                ...ShortcutManager.registeredShortcuts.map(s => ({
                        keyPatterns: s.keyPatterns,
                        options: s.options,
                }))
            ]
        }

        return [...ShortcutManager.registeredShortcuts];
    },

    OnKeyDown(ev: KeyboardEvent) {
        if (lastEvent == ev) {
            return; // Prevent double triggering from the iframe and parent window.
        }
        lastEvent = ev;

        const target = ev.target as Element;
        const eventKey = ev.key?.toLowerCase();

        // Only track keyboard shortcuts for the body containing the SVG-Editor
        if (target.nodeName !== "BODY" || !eventKey) {
            return;
        }

        for (const shortcut of ShortcutManager.registeredShortcuts) {
            for (const kp of shortcut.keyPatterns) {
                if (eventKey !== kp.key) {
                    continue;
                }

                if ((kp.shift ?? false) != ev.shiftKey) {
                    continue;
                }

                if ((kp.alt ?? false) != ev.altKey) {
                    continue;
                }

                if ((kp.ctrl ?? false) != (isMac() ? ev.metaKey : ev.ctrlKey)) {
                    continue;
                }

                shortcut.action(ev);
                if (shortcut.options.dontPreventDefault !== true) {
                    ev.preventDefault();
                }

                break;
            }
        }
    },

    KeyPatternToString(keyPattern: IKeyPattern): string {
        if (!keyPattern) {
            console.error(`Tried to convert ${keyPattern} to a string, but it is not a valid key pattern!`);
            return "";
        }

        let ret = (keyPattern?.displayKey ?? keyPattern.key).toUpperCase();

        if (keyPattern.shift) {
            ret = `Shift + ${ret}`;
        }

        if (keyPattern.alt) {
            ret = `${isMac() ? '⌥' : 'Alt'} + ${ret}`;
        }

        if (keyPattern.ctrl) {
            ret = `${isMac() ? '⌘' : 'Ctrl'} + ${ret}`;
        }

        return ret;
    },

    GetShortcutFromAlias(alias?: string) {
        if (!alias) {
            return null;
        }

        alias = alias.toLowerCase();


        for (const s of this.registeredShortcuts) {
            if (s.options.alias?.toLowerCase() == alias) {
                return s;
            }
        }

        return null;
    },

    ShortcutAliasToKeyPatternString(alias?: string, limitCount: number = 128): string {
        const s = ShortcutManager.GetShortcutFromAlias(alias);
        let shortcut = "";

        if (s) {
            let c = 0;
            for (const kp of s.keyPatterns) {
                if (++c > limitCount) {
                    break;
                }

                if (shortcut.length > 0) {
                    shortcut += " / ";
                }

                shortcut += ShortcutManager.KeyPatternToString(kp);
            }
        }

        return shortcut;
    }
}

window["_zaa_services"] = window["_zaa_services"] || {};
window["_zaa_services"]["shortcutManager"] = ShortcutManager; // For Blazor interop

export default ShortcutManager;
